/**
 * @prettier
 */
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@pidedirecto/ui';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GetAppContextSignedInApiResponse } from 'src/api/pidedirecto/getAppContextSignedInApi';
import { SelectAuthenticationTypeDialog } from 'src/components/cart/SelectAuthenticationTypeDialog';
import { Dialog } from 'src/components/Dialog';
import { ChangePasswordDialog } from 'src/components/dialog/customer/ChangePasswordDialog';
import { DialogActions } from 'src/components/DialogActions';
import { Form } from 'src/components/form/Form';
import { FormPrefixPhoneNumberField } from 'src/components/form/FormPrefixPhoneNumberField';
import { Text } from 'src/components/Text';
import { AuthenticationTypes, type AuthenticationType } from 'src/constants/AuthenticationType';
import { AwsFacade } from 'src/facade/aws/AwsFacade';
import { translate } from 'src/i18n/translate';
import { actions } from 'src/reducers';
import { useAction } from 'src/utils/react/useAction';
import { useSelector } from 'src/utils/react/useSelector';

export function SignInDialog({ onSuccess }: Props): React.ReactElement {
    const classes = useStyles();

    const form = useForm();

    const [mobileNumber, setMobileNumber] = useState('');
    const [error, setError] = useState<{ errorType: string; message: string }>();
    const [selectAuthenticationTypeDialogOpen, setSelectAuthenticationTypeDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [shouldRecoverPassword, setShouldRecoverPassword] = useState(false);
    const [doesUserExists, setDoesUserExists] = useState(true);
    const [changePasswordDialogState, setChangePasswordDialogState] = useState({ open: false });

    const open = useSelector((state) => state.app.signInDialog.open);

    const openSignInDialog = useAction(actions.openSignInDialog);
    const closeSignInDialog = useAction(actions.closeSignInDialog);
    const openEnterVerificationCodeDialog = useAction(actions.openEnterVerificationCodeDialog);
    const openEnterPasswordDialog = useAction(actions.openEnterPasswordDialog);
    const closeEnterPasswordDialog = useAction(actions.closeEnterPasswordDialog);

    const formMobileNumber = form.watch('mobileNumber');

    useEffect(() => {
        if (open) setError(undefined);
        else setShouldRecoverPassword(false);
    }, [open]);

    const signInWithCode = async () => {
        setMobileNumber(formMobileNumber);
        closeSignInDialog();
        setSelectAuthenticationTypeDialogOpen(true);
    };

    const signInWithPassword = async (form: any) => {
        const mobileNumber = form?.mobileNumber || formMobileNumber;
        setIsSubmitting(true);
        const response = await AwsFacade.requestSignUpSignIn(mobileNumber, AuthenticationTypes.PASSWORD);
        setIsSubmitting(false);
        if (response.error) {
            if (response.error.errorType === 'USER_DOES_NOT_EXISTS_ERROR') setDoesUserExists(false);
            setError(response.error);
            return;
        }
        closeSignInDialog();
        openEnterPasswordDialog({ mobileNumber, onForgotPassword: forgotPassword });
    };

    const forgotPassword = (mobileNumber: string) => {
        setMobileNumber(mobileNumber);
        setShouldRecoverPassword(true);
        closeEnterPasswordDialog();
        setSelectAuthenticationTypeDialogOpen(true);
    };

    const onSelectAuthenticationType = async (_authenticationType: AuthenticationType) => {
        openSignInDialog();
        setIsSubmitting(true);
        const response = await AwsFacade.requestSignUpSignIn(mobileNumber, _authenticationType);
        setIsSubmitting(false);
        if (response.error) {
            setError(response.error);
            return;
        }
        closeSignInDialog();
        openEnterVerificationCodeDialog({
            mobileNumber,
            authenticationType: _authenticationType,
            onSuccess: handleSignInWithCodeSucceeded,
        });
    };

    const handleSignInWithCodeSucceeded = (appContext: GetAppContextSignedInApiResponse) => {
        if (shouldRecoverPassword || !appContext?.hasPassword) return openChangePasswordDialog();
        onSuccess?.();
    };

    const closeSelectAuthenticationTypeDialog = () => {
        setSelectAuthenticationTypeDialogOpen(false);
    };

    const openChangePasswordDialog = () => {
        setChangePasswordDialogState({ open: true });
    };

    const closeChangePasswordDialog = () => {
        setChangePasswordDialogState({ open: false });
    };

    return (
        <>
            <Dialog classes={{ dialog: classes.dialog, content: classes.dialog }} open={open} loading={isSubmitting} title={translate('Sign In / Sign Up')} onClose={closeSignInDialog}>
                <Form form={form} onSubmit={signInWithPassword} id={'form'}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            {!error && <Text style={{ textAlign: 'center' }}>{translate('A verification code will be sent to you in a text message')}</Text>}
                        </Grid>
                        <Grid item xs={12}>
                            <FormPrefixPhoneNumberField name={'mobileNumber'} disabled={isSubmitting} helperText={translate('Make sure to select the flag of your country')} required />
                        </Grid>
                        <Grid item xs={12}>
                            {!!error && <Text>{translate(error.message)}</Text>}
                        </Grid>
                    </Grid>
                    <DialogActions>
                        <Button variant='secondary' classes={{ button: classes.button }} onClick={signInWithCode} disabled={isSubmitting}>
                            {isSubmitting && <CircularProgress size={40} style={{ position: 'absolute' }} />}
                            {translate('Use code')}
                        </Button>
                        {doesUserExists && (
                            <Button type='submit' classes={{ button: classes.button }} disabled={isSubmitting}>
                                {isSubmitting && <CircularProgress size={40} style={{ position: 'absolute' }} />}
                                {translate('Use password')}
                            </Button>
                        )}
                    </DialogActions>
                </Form>
            </Dialog>
            <SelectAuthenticationTypeDialog open={selectAuthenticationTypeDialogOpen} onClose={closeSelectAuthenticationTypeDialog} onSelectAuthenticationType={onSelectAuthenticationType} />
            <ChangePasswordDialog open={changePasswordDialogState.open} onClose={closeChangePasswordDialog} onSuccess={onSuccess} />
        </>
    );
}
const useStyles = makeStyles((theme) => ({
    dialog: {
        borderRadius: 8,
        borderBottomRightRadius: 18,
        borderBottomLeftRadius: 18,
    },
    button: {
        flexGrow: 1,
    },
}));

type Props = {
    onSuccess?: Function;
};
